// SpeedAssign Core (Feb 2026 · Slice 1F) — final nav = Day | Coaches | Fleet | Settings.
// /schedule redirects to /day; DefaultLandingRedirect points to /day.
// Secondary routes (Bookings, Manifests, Availability, Coordinators, Access)
// remain available and are surfaced via the Settings hub.
import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SelectedDateProvider, URLDateSync } from "@/lib/selectedDate";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import ForcePasswordChange from "@/pages/ForcePasswordChange";
import Layout from "@/components/Layout";

const Coaches = lazy(() => import("@/pages/Coaches"));
const Availability = lazy(() => import("@/pages/Availability"));
const Bookings = lazy(() => import("@/pages/Bookings"));
const Access = lazy(() => import("@/pages/Access"));
const Coordinators = lazy(() => import("@/pages/Coordinators"));
const Manifests = lazy(() => import("@/pages/Manifests"));
const PrintPrepPage = lazy(() => import("@/pages/PrintPrepPage"));
const PrintOrganizer = lazy(() => import("@/pages/PrintOrganizer"));

const Schedule = lazy(() => import("@/pages/Schedule"));
const Fleet = lazy(() => import("@/pages/Fleet"));
const Settings = lazy(() => import("@/pages/Settings"));
// Feb 2026 · Program Builder V1 (PB-2).
const Programs = lazy(() => import("@/pages/Programs"));
const ExperienceRuns = lazy(() => import("@/pages/ExperienceRuns"));
// Feb 2026 · Slice 2 · Classification Rules Admin.
const ClassificationRules = lazy(() => import("@/pages/ClassificationRules"));
// Feb 2026 · Slice 3 · Import History & Recovery.
const ImportHistory = lazy(() => import("@/pages/ImportHistory"));
// Feb 2026 · Slice 5 · Corporate Staffing Workspace.
const CorporateWorkspace = lazy(() => import("@/pages/CorporateWorkspace"));
// Feb 2026 · Slice 6 · Operator Activity Feed.
const Activity = lazy(() => import("@/pages/Activity"));

// Batch 5 · Weekly Assignment Board.
const AssignmentBoard = lazy(() => import("@/pages/AssignmentBoard"));
// Release 2 (Feb 2026) — Arrival Rules admin.
const ArrivalRules = lazy(() => import("@/pages/ArrivalRules"));
// Release 5 (Feb 2026) — Supervisor Week Overview.
const Overview = lazy(() => import("@/pages/Overview"));

const PageLoader = () => (
  <div className="min-h-[40vh] flex items-center justify-center">
    <div className="label-tag animate-pulse">Loading…</div>
  </div>
);
const lazyPage = (node) => <Suspense fallback={<PageLoader />}>{node}</Suspense>;

// Path scopes per role.
// Slice 1F: /day is the primary workspace. /schedule redirects to it
// (existing bookmarks preserved). Settings gates the utility surfaces.
const FM_ALLOWED_PATHS = new Set([
  "/day", "/schedule", "/fleet", "/manifests", "/manifests/print-prep",
  "/manifests/print-organizer",
  "/print-organizer",  // legacy redirect target — allowlisted so the redirect renders
]);
const SHOP_ALLOWED_PATHS = new Set([
  "/fleet", "/manifests", "/manifests/print-prep",
  "/manifests/print-organizer", "/print-organizer",
]);
const CAR_ONLY_TITLES = new Set(["fleet_manager", "logistics_lead"]);
const SHOP_TITLES = new Set(["shop_manager", "shop_member"]);

// Saved bookmarks retain their date and action when entering the canonical page.
function LegacyRouteRedirect({ to }) {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: to, search, hash }} replace />;
}

const Protected = ({ children, path }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background"
           data-testid="protected-loading">
        <div className="label-tag animate-pulse">Loading operations…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  if (SHOP_TITLES.has(user.title || "") && path && !SHOP_ALLOWED_PATHS.has(path)) {
    return <Navigate to="/fleet" replace />;
  }
  if (CAR_ONLY_TITLES.has(user.title || "") && path && !FM_ALLOWED_PATHS.has(path)) {
    return <Navigate to="/day" replace />;
  }
  return <Layout>{children}</Layout>;
};

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* Slice 1F: canonical Day Workspace route. */}
      <Route path="/day" element={
        <Protected path="/day">{lazyPage(<Schedule />)}</Protected>
      } />
      {/* Release 5 (Feb 2026) — Supervisor Week Overview. `/ops` is a
          safe alias so bookmarks / hand-typed navigation land on the
          same page. The default landing route (`/day`) is UNCHANGED. */}
      <Route path="/overview" element={
        <Protected path="/overview">{lazyPage(<Overview />)}</Protected>
      } />
      <Route path="/ops" element={<LegacyRouteRedirect to="/overview" />} />
      {/* Legacy /schedule + /my-day bookmarks redirect to /day. */}
      <Route path="/schedule" element={<LegacyRouteRedirect to="/day" />} />
      <Route path="/my-day" element={<LegacyRouteRedirect to="/day" />} />
      <Route path="/manifests" element={<Protected path="/manifests">{lazyPage(<Manifests />)}</Protected>} />
      <Route path="/manifests/print-prep" element={<Protected path="/manifests/print-prep">{lazyPage(<PrintPrepPage />)}</Protected>} />
      {/* Phase M-A (Feb 2026): canonical organizer route moved under
          /manifests. Legacy /print-organizer keeps redirect below so
          bookmarks + saved deep-links keep working. */}
      <Route path="/manifests/print-organizer" element={<Protected path="/manifests/print-organizer">{lazyPage(<PrintOrganizer />)}</Protected>} />
      <Route path="/print-organizer" element={<LegacyRouteRedirect to="/manifests/print-organizer" />} />
      <Route path="/fleet" element={<Protected path="/fleet">{lazyPage(<Fleet />)}</Protected>} />
      <Route path="/coaches" element={<Protected path="/coaches">{lazyPage(<Coaches />)}</Protected>} />
      {/* Feb 2026 · Program Builder V1 — library + editor + booking mappings. */}
      <Route path="/programs/*" element={<Protected path="/programs">{lazyPage(<Programs />)}</Protected>} />
      {/* Feb 2026 · Experience Run Foundation V1 — feature-flagged
          inspector; the page itself hides everything when the
          `/api/config.features.experience_runs_v1_enabled` flag is
          false so route access alone is not a data leak. */}
      <Route path="/experience-runs" element={<Protected path="/experience-runs">{lazyPage(<ExperienceRuns />)}</Protected>} />
      <Route path="/experience-runs/:runId" element={<Protected path="/experience-runs">{lazyPage(<ExperienceRuns />)}</Protected>} />
      <Route path="/settings" element={<Protected path="/settings">{lazyPage(<Settings />)}</Protected>} />
      <Route path="/settings/classification-rules" element={<Protected path="/settings/classification-rules">{lazyPage(<ClassificationRules />)}</Protected>} />
      <Route path="/settings/import-history" element={<Protected path="/settings/import-history">{lazyPage(<ImportHistory />)}</Protected>} />
      {/* Release 2 (Feb 2026) — non-technical Arrival Rules admin. */}
      <Route path="/settings/arrival-rules" element={<Protected path="/settings/arrival-rules">{lazyPage(<ArrivalRules />)}</Protected>} />
      <Route path="/availability" element={<Protected path="/availability">{lazyPage(<Availability />)}</Protected>} />
      <Route path="/bookings" element={<Protected path="/bookings">{lazyPage(<Bookings />)}</Protected>} />
      <Route path="/coordinators" element={<Protected path="/coordinators">{lazyPage(<Coordinators />)}</Protected>} />
      <Route path="/access" element={<Protected path="/access">{lazyPage(<Access />)}</Protected>} />
      {/* Feb 2026 · Slice 5 · Corporate Staffing Workspace. */}
      <Route path="/corporate" element={<Protected path="/corporate">{lazyPage(<CorporateWorkspace />)}</Protected>} />
      {/* Feb 2026 · Slice 6 · Operator Activity Feed. */}
      <Route path="/activity" element={<Protected path="/activity">{lazyPage(<Activity />)}</Protected>} />
      {/* Batch 5 · Weekly Assignment Board. */}
      <Route path="/assignment-board" element={<Protected path="/assignment-board">{lazyPage(<AssignmentBoard />)}</Protected>} />
      <Route path="*" element={<DefaultLandingRedirect />} />
    </Routes>
  );
}

// SpeedAssign Core (Feb 2026 · Slice 1F) — Day is the default landing.
const DefaultLandingRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background"
           data-testid="protected-loading">
        <div className="label-tag animate-pulse">Loading operations…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  const title = user.title || "";
  if (SHOP_TITLES.has(title)) return <Navigate to="/fleet" replace />;
  return <Navigate to="/day" replace />;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SelectedDateProvider>
          <BrowserRouter>
            <URLDateSync />
            <AppRoutes />
            <Toaster position="top-right" />
          </BrowserRouter>
        </SelectedDateProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
